/**
 * Dues — the whole roster sorted by how much attention it needs:
 * never-paid, then lapsed, then due soon, then paid, then life members.
 *
 * Actions appear only on rows where money is actually owed. Everyone else
 * gets "Nothing due" in grey, which is the point.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  clubFees,
  duesSortRank,
  duesState,
  formatDate,
  formatMoney,
  outstandingTotal,
  payable,
  TIERS,
} from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { PayLinkSheet, RecordPaymentSheet } from '../../components/sheets/MoneySheets';
import { Button, Empty, Pill, Table, ViewTitle } from '../../components/ui';

/*
 * The dashboard's money counters deep-link here: "Outstanding" wants everyone
 * who owes right now (never-paid AND lapsed — the same set outstandingTotal
 * sums), "Dues lapsed" wants only the lapsed. NOTE: the dues key 'pending'
 * means "never paid", nothing to do with the `pending` MEMBER status.
 */
const VIEWS = {
  all: { label: 'Everyone', match: () => true },
  outstanding: {
    label: 'Outstanding',
    match: (m) => ['pending', 'lapsed'].includes(duesState(m).key),
  },
  lapsed: { label: 'Lapsed', match: (m) => duesState(m).key === 'lapsed' },
};

export function DuesPage() {
  const { db } = useClubData();
  const { openSheet } = useUI();
  const [params] = useSearchParams();
  const urlView = params.get('filter');
  const [view, setView] = useState(VIEWS[urlView] ? urlView : 'all');

  // Live roster only. Members who have left are kept forever (bibs are never
  // reused) but they owe nothing — chasing them for dues was wrong. `pending`
  // members are pipeline, not dues: they are chased on Membership requests.
  const roster = useMemo(
    () => db.members.filter((m) => m.status !== 'left' && m.status !== 'pending'),
    [db.members],
  );

  const rows = roster
    .filter(VIEWS[view].match)
    .sort((a, b) => {
      const rank = duesSortRank(a) - duesSortRank(b);
      if (rank !== 0) return rank;
      return (a.duesPaidTill ?? '').localeCompare(b.duesPaidTill ?? '');
    });

  const fees = clubFees(db.meta.fees);
  const owed = outstandingTotal(roster, db.meta.fees);
  const lapsedCount = roster.filter(VIEWS.lapsed.match).length;
  const owingCount = roster.filter(VIEWS.outstanding.match).length;

  return (
    <>
      <ViewTitle
        title="Dues & payments"
        sub={`Annual ₹${fees.annual.join} to join · ₹${fees.annual.renew} to renew · send a link or record cash`}
      />

      {/* Who is on screen. The counts ride on the chips so the committee can
          see the size of each problem before choosing it. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
        <div className="inline-flex flex-wrap gap-1 border border-line bg-ground p-1">
          {Object.entries(VIEWS).map(([key, v]) => {
            const on = view === key;
            const count =
              key === 'all' ? roster.length : key === 'lapsed' ? lapsedCount : owingCount;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                data-label={`${v.label} ${count}`}
                className={`text-stable whitespace-nowrap px-3 py-1.5 font-market text-[10.5px] uppercase tracking-wide transition-colors duration-200 ${
                  on
                    ? 'bg-white font-bold text-ink-700 shadow-sm'
                    : 'text-copy-muted hover:text-ink-700'
                }`}
              >
                {v.label} {count}
              </button>
            );
          })}
        </div>
        {owed > 0 && (
          <div className="font-market text-[11px] tabular-nums text-flag-600">
            {formatMoney(owed)} outstanding · {owingCount} member{owingCount === 1 ? '' : 's'}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <Empty title="All clear">Nobody matches this view.</Empty>
      ) : (
        <DuesTable rows={rows} openSheet={openSheet} />
      )}

      {/* Fees and the payment setup live on their own tab now — this line is
          the pointer for a committee member standing here mid-chase. */}
      <p className="mt-8 text-[12px] text-copy-muted">
        Amounts and how members pay you (QR, bank account, Razorpay) are set
        under{' '}
        <Link to="/a/settings" className="font-semibold text-gold-800 underline underline-offset-2">
          Settings
        </Link>
        .
      </p>
    </>
  );
}


function DuesTable({ rows, openSheet }) {
  return (
    <>

      <Table
        head={
          <tr>
            <th>Bib</th>
            <th>Member</th>
            <th>Tier</th>
            <th>Paid till</th>
            <th>Status</th>
            <th />
          </tr>
        }
      >
        {rows.map((m) => {
          const dues = duesState(m);
          return (
            <tr key={m.id}>
              <td>{m.bib}</td>
              <td className="font-market font-bold text-ink-700">{m.name}</td>
              <td>{TIERS[m.tier].label}</td>
              <td>{m.tier === 'life' ? '—' : formatDate(m.duesPaidTill)}</td>
              <td>
                <Pill tone={dues.cls}>{dues.label}</Pill>
              </td>
              <td>
                {payable(m) ? (
                  <div className="flex gap-1.5">
                    <Button tone="p" small onClick={() => openSheet(<PayLinkSheet member={m} />)}>
                      Pay link
                    </Button>
                    <Button
                      tone="g"
                      small
                      onClick={() => openSheet(<RecordPaymentSheet member={m} />)}
                    >
                      Cash
                    </Button>
                  </div>
                ) : (
                  <span className="font-market text-[10.5px] text-copy-label">
                    {m.tier === 'life' ? '—' : 'Nothing due'}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </Table>
    </>
  );
}

